import { useEffect, useState } from "react";
import { NavLink, Navigate, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingBag, Users, FileText, Home, Star, Ticket, Settings, LogOut, Radio, ShieldCheck, Building2, UserCog, ScrollText, Menu, X, Activity, ShoppingCart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard", superOnly: false },
  { to: "/admin/orders", icon: ShoppingBag, label: "Ordini", superOnly: false },
  { to: "/admin/abandoned-carts", icon: ShoppingCart, label: "Carrelli abbandonati", superOnly: false },
  { to: "/admin/tracking", icon: Activity, label: "Tracking", superOnly: false },
  { to: "/admin/customers", icon: Users, label: "Clienti", superOnly: false },
  { to: "/admin/suppliers", icon: Building2, label: "Fornitori", superOnly: false },
  { to: "/admin/products", icon: Package, label: "Prodotti", superOnly: false },
  { to: "/admin/settings", icon: Settings, label: "Impostazioni", superOnly: false },
  { to: "/admin/team", icon: UserCog, label: "Team Admin", superOnly: true },
  { to: "/admin/activity-log", icon: ScrollText, label: "Log Attività", superOnly: true },
  { to: "/admin/blog", icon: FileText, label: "Blog", superOnly: true },
  { to: "/admin/homepage", icon: Home, label: "Homepage", superOnly: true },
  { to: "/admin/reviews", icon: Star, label: "Recensioni", superOnly: true },
  { to: "/admin/coupons", icon: Ticket, label: "Marketing", superOnly: true },
];

export default function AdminLayout({ children }) {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close the drawer whenever route changes.
  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-xd-muted">Caricamento…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const isSuper = user.role === "superadmin";
  const items = NAV.filter((n) => (n.superOnly ? isSuper : true));
  const doLogout = () => { logout(); nav("/admin/login"); };
  const activeLabel = items.find((n) => (n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to)))?.label || "Admin";

  const SidebarInner = (
    <>
      <div className="p-5 border-b border-xd-darkBorder">
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-xd-cyan to-xd-cyanHover flex items-center justify-center"><Radio className="w-4 h-4 text-xd-dark" /></span>
            <span className="font-display font-semibold">audrivio</span>
          </div>
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            data-testid="admin-drawer-close"
            className="md:hidden w-9 h-9 rounded-lg text-slate-300 hover:bg-xd-dark2 flex items-center justify-center"
            aria-label="Chiudi menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="text-[10px] font-mono-tech uppercase tracking-widest text-xd-cyan mt-1 ml-10">admin panel</div>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {items.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.to.split('/').pop() || 'dashboard'}`} className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "bg-xd-cyan/10 text-xd-cyan" : "hover:bg-xd-dark2 text-slate-300"}`}>
            <n.icon className="w-4 h-4" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-xd-darkBorder">
        <div className="text-xs text-slate-400 px-3 py-2">
          <div className="font-medium text-slate-200 truncate">{user.name}</div>
          <div className="text-[11px] truncate">{user.email}</div>
          <div className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-xd-dark2 border border-xd-darkBorder text-[10px] font-mono-tech uppercase tracking-widest" data-testid="admin-role-badge">
            {isSuper ? <ShieldCheck className="w-3 h-3 text-xd-cyan" /> : null}
            <span className={isSuper ? "text-xd-cyan" : "text-slate-400"}>{isSuper ? "super admin" : "admin"}</span>
          </div>
        </div>
        <button type="button" onClick={doLogout} data-testid="admin-logout" className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-xd-dark2 text-slate-300">
          <LogOut className="w-4 h-4" /> Esci
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-xd-light font-admin md:flex">
      {/* Desktop sidebar — always visible from md up */}
      <aside className="hidden md:flex w-60 bg-xd-dark text-slate-200 flex-col border-r border-xd-darkBorder sticky top-0 h-screen shrink-0">
        {SidebarInner}
      </aside>

      {/* Mobile top bar with hamburger */}
      <div className="md:hidden sticky top-0 z-30 bg-xd-dark text-slate-200 border-b border-xd-darkBorder flex items-center justify-between px-3 h-14">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          data-testid="admin-drawer-open"
          className="w-11 h-11 rounded-lg hover:bg-xd-dark2 flex items-center justify-center"
          aria-label="Apri menu"
        >
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-xd-cyan to-xd-cyanHover flex items-center justify-center"><Radio className="w-3.5 h-3.5 text-xd-dark" /></span>
          <span className="font-display font-semibold text-white text-sm">{activeLabel}</span>
        </div>
        <div className="w-11 h-11" aria-hidden />
      </div>

      {/* Mobile drawer overlay */}
      {drawerOpen && (
        <>
          <div
            onClick={() => setDrawerOpen(false)}
            data-testid="admin-drawer-backdrop"
            className="fixed inset-0 z-40 bg-black/60 md:hidden"
            aria-hidden
          />
          <aside
            data-testid="admin-mobile-drawer"
            className="fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-xd-dark text-slate-200 flex flex-col border-r border-xd-darkBorder md:hidden shadow-2xl"
          >
            {SidebarInner}
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0 p-4 md:p-6 lg:p-8">
        {children}
      </main>
    </div>
  );
}
